import { HttpErrorResponse } from '@angular/common/http';

import { createSelector } from '@ngrx/store';

import { UserSelectors } from '@recruitee/account/data';
import { FeatureFlag, FeatureFlagSelectors } from '@recruitee/feature-flags/data';
import {
  Ability,
  FullCandidate,
  Placement,
  ShareCandidateWithTeamResponse,
  User,
} from '@recruitee/http/types';
import {
  CorrelationId,
  createEntityCollectionStore,
  createSelectorDeepEqual,
  EntityState,
  getEntityCollectionActions,
  memoizeSelector,
  ngrxEvent,
  PickActions,
  Reference,
} from '@recruitee/ngrx';
import { PlacementSelectors } from '@recruitee/placements/data';
import { CandidateReferrerSelectors } from '@recruitee/referrals/data';

import {
  CopyToCompanyParams,
  DeleteParsedCvParams,
  SaveParams,
  ShareCandidateWithTeamRequest,
  UpdateFileParams,
  UpdateParseCvParams,
} from './full-candidate-http-client';

const idSelector = ({ id }: { id: number }) => id;
const { actions, selectors, reducer, namespace } = createEntityCollectionStore<FullCandidate>()(
  'FullCandidate',
  {
    idSelector,
    references: makeRef =>
      <Array<Reference<FullCandidate>>>[
        makeRef('adminId', 'admin', UserSelectors.byId),
        makeRef('adminIds', 'admins', UserSelectors.byIds),
        makeRef('placements', 'placements', (placements: Placement[]) =>
          PlacementSelectors.byIdsWithReferences((placements || []).map(({ id }) => id)),
        ),
        makeRef('referralReferrersIds', 'referralReferrers', CandidateReferrerSelectors.byIds),
      ],
    // eslint-disable-next-line max-lines-per-function
    customActions: adapter => {
      const defaultActions = getEntityCollectionActions(adapter, idSelector);
      const fetchOne = (state: EntityState<FullCandidate>, payload: { id: number }) =>
        defaultActions.fetchOne(state, payload);
      const fetchOneSuccess = (
        state: EntityState<FullCandidate>,
        payload: { data: FullCandidate },
      ) => defaultActions.fetchOneSuccess(state, payload);
      const fetchOneFailed = (
        state: EntityState<FullCandidate>,
        payload: { error: HttpErrorResponse; id: number },
      ) => defaultActions.fetchOneFailed(state, payload);

      return {
        fetchOne: fetchOne,
        fetchOneSuccess: fetchOneSuccess,
        fetchOneFailed: fetchOneFailed,

        bufferedFetchOne: fetchOne,

        createOnePessimistic: (state, payload: { params: SaveParams } & Required<CorrelationId>) =>
          adapter.markAsPending(state, {
            entities: [payload.correlationId],
            scope: null,
          }),

        updateOnePessimistic: (state, payload: { params: SaveParams }) =>
          defaultActions.updateOnePessimistic(state, { data: { id: payload.params.data.id! } }),

        removePhoto: fetchOne,
        removePhotoSuccess: fetchOneSuccess,
        removePhotoFailed: fetchOneFailed,

        updateCv: (state, payload: UpdateFileParams) => fetchOne(state, payload),
        updateCvSuccess: fetchOneSuccess,
        updateCvFailed: fetchOneFailed,

        updateParseCv: (state, payload: UpdateParseCvParams) => fetchOne(state, payload),
        updateParseCvSuccess: fetchOneSuccess,
        updateParseCvFailed: fetchOneFailed,

        deleteCv: fetchOne,
        deleteCvSuccess: fetchOneSuccess,
        deleteCvFailed: fetchOneFailed,

        deleteParsedCv: (state, payload: DeleteParsedCvParams) => fetchOne(state, payload),
        deleteParsedCvSuccess: fetchOneSuccess,
        deleteParsedCvFailed: fetchOneFailed,

        reparseCv: fetchOne,
        reparseCvSuccess: fetchOneSuccess,
        reparseCvFailed: fetchOneFailed,

        updateCoverLetterFile: (state, payload: UpdateFileParams) => fetchOne(state, payload),
        updateCoverLetterFileSuccess: fetchOneSuccess,
        updateCoverLetterFileFailed: fetchOneFailed,

        deleteCoverLetterFile: (state, payload: { candidateId: number }) =>
          fetchOne(state, { id: payload.candidateId }),
        deleteCoverLetterFileSuccess: fetchOneSuccess,
        deleteCoverLetterFileFailed: fetchOneFailed,

        coverLetterPromptReplaceExistingFile: ngrxEvent<void>(),
        coverLetterPromptReplaceExistingFileSuccess: ngrxEvent<void>(),
        coverLetterPromptReplaceExistingFileFailed: ngrxEvent<void>(),

        coverLetterPromptReplaceTextWithFile: ngrxEvent<void>(),
        coverLetterPromptReplaceTextWithFileSuccess: ngrxEvent<void>(),
        coverLetterPromptReplaceTextWithFileFailed: ngrxEvent<void>(),

        coverLetterPromptDelete: ngrxEvent<void>(),
        coverLetterPromptDeleteSuccess: ngrxEvent<void>(),
        coverLetterPromptDeleteFailed: ngrxEvent<void>(),

        view: fetchOne,
        viewSuccess: fetchOneSuccess,
        viewFailed: fetchOneFailed,

        merge: ngrxEvent<{ id: number }>(),
        mergeSuccess: ngrxEvent<{ uuid: string }>(),
        mergeFailed: ngrxEvent<{ error: HttpErrorResponse; id: number }>(),

        follow: fetchOne,
        followSuccess: fetchOneSuccess,
        followFailed: fetchOneFailed,

        unfollow: fetchOne,
        unfollowSuccess: fetchOneSuccess,
        unfollowFailed: fetchOneFailed,

        retrieve: fetchOne,
        retrieveSuccess: fetchOneSuccess,
        retrieveFailed: fetchOneFailed,

        copyToCompany: (
          state,
          payload: { params: CopyToCompanyParams } & Required<CorrelationId>,
        ) => adapter.markAsPending(state, { scope: null, entities: [payload.correlationId] }),
        copyToCompanySuccess: defaultActions.createOnePessimisticSuccess,
        copyToCompanyFailed: defaultActions.createOnePessimisticFailed,

        shareWithTeam: ngrxEvent<ShareCandidateWithTeamRequest>(),
        shareWithTeamSuccess: ngrxEvent<ShareCandidateWithTeamResponse['candidate']>(),
        shareWithTeamFailed: ngrxEvent<{ error: HttpErrorResponse }>(),
      };
    },
  },
);

const FullCandidateActions: PickActions<
  typeof actions,
  | 'fetchOne'
  | 'updateOnePessimistic'
  | 'createOnePessimistic'
  | 'removePhoto'
  | 'updateCv'
  | 'updateParseCv'
  | 'deleteCv'
  | 'deleteParsedCv'
  | 'reparseCv'
  | 'view'
  | 'merge'
  | 'follow'
  | 'unfollow'
  | 'retrieve'
  | 'copyToCompany'
  | 'updateCoverLetterFile'
  | 'deleteCoverLetterFile'
  | 'coverLetterPromptReplaceExistingFile'
  | 'coverLetterPromptReplaceTextWithFile'
  | 'coverLetterPromptDelete'
  | 'shareWithTeam'
> &
  Pick<typeof actions, 'bufferedFetchOne'> = actions;

// Tagging fns

const userTaggingCommonFn = (user: User, candidate: FullCandidate) =>
  user.roleAbilities.includes(Ability.ViewCandidates) ||
  user.roleAbilities.includes(Ability.ViewAllJobs) ||
  (candidate.placements &&
    candidate.placements.some(
      placement =>
        (user.offerIds || []).includes(placement.offerId) ||
        (user.accessiblePlacementIds || []).includes(placement.id),
    ));

const taggedAdmins = createSelector(UserSelectors.entities(), users =>
  users.map(user => ({ ...user, hasCandidateAccess: true })),
);
const tagAdminsWithViewHiredCandidates = (users: User[], candidate: FullCandidate | null) =>
  candidate
    ? users.map(user => ({
        ...user,
        hasCandidateAccess:
          user.roleAbilities.includes(Ability.ViewHiredCandidates) &&
          userTaggingCommonFn(user, candidate),
      }))
    : users;
const tagAdminsWithCandidateId = (users: User[], candidate: FullCandidate | null) =>
  candidate
    ? users.map(user => ({
        ...user,
        hasCandidateAccess: userTaggingCommonFn(user, candidate),
      }))
    : users;
const tagAdminsWithAccessBasedOnHiringStatus = (users: User[], candidate: FullCandidate | null) =>
  candidate?.placements?.some(p => !!p.hiredById)
    ? tagAdminsWithViewHiredCandidates(users, candidate)
    : tagAdminsWithCandidateId(users, candidate);

const tagAdminsWithAccessToOfferOrCandidate = (
  users: User[],
  candidateId: number | undefined,
  offerId: number,
) =>
  users.map(user => ({
    ...user,
    hasCandidateAccess: user.viewAllJobs || !!user.offerIds?.includes(offerId), // TODO: Rewrite to check accessiblePlacementIds
  }));

const tagAdminsForOffer = (users: User[], candidate: FullCandidate | null, offerId: number) =>
  candidate?.placements?.some(p => !!p.hiredById)
    ? tagAdminsWithViewHiredCandidates(users, candidate)
    : tagAdminsWithAccessToOfferOrCandidate(users, candidate?.id, offerId);

const commonAdminsTagged = memoizeSelector((candidateId: number) =>
  createSelector(taggedAdmins, selectors.byIdWithReferences(candidateId), (users, candidate) =>
    tagAdminsWithAccessBasedOnHiringStatus(users, candidate),
  ),
);

const offerAdminsTagged = memoizeSelector((props: { candidateId: number; offerId?: number }) =>
  createSelector(
    UserSelectors.entities(),
    selectors.byIdWithReferences(props.candidateId),
    (users, candidate) =>
      props.offerId
        ? tagAdminsForOffer(users, candidate, props.offerId)
        : tagAdminsWithAccessBasedOnHiringStatus(users, candidate),
  ),
);

const filterTaggedUsers = (users: User[], hasCandidateAccess: boolean) =>
  users.filter(user => user.hasCandidateAccess === hasCandidateAccess);

// Selectors for tagged admins
const commonAdminsWithAccess = memoizeSelector((candidateId: number) =>
  createSelector(commonAdminsTagged(candidateId), users => filterTaggedUsers(users, true)),
);
const commonAdminsWithoutAccess = memoizeSelector((candidateId: number) =>
  createSelector(commonAdminsTagged(candidateId), users => filterTaggedUsers(users, false)),
);

const commonAdminsGrouped = memoizeSelector((candidateId: number) =>
  createSelector(
    commonAdminsWithAccess(candidateId),
    commonAdminsWithoutAccess(candidateId),
    (adminsWithAccess, adminsWithoutAccess) => ({ adminsWithAccess, adminsWithoutAccess }),
  ),
);

const offerAdminsWithAccess = memoizeSelector((props: { candidateId: number; offerId?: number }) =>
  createSelector(offerAdminsTagged(props), users => filterTaggedUsers(users, true)),
);

const offerAdminsWithoutAccess = memoizeSelector(
  (props: { candidateId: number; offerId?: number }) =>
    createSelector(offerAdminsTagged(props), users => filterTaggedUsers(users, false)),
);

const offerAdminsGrouped = memoizeSelector((props: { candidateId: number; offerId?: number }) =>
  createSelector(
    offerAdminsWithAccess(props),
    offerAdminsWithoutAccess(props),
    (adminsWithAccess, adminsWithoutAccess) => ({ adminsWithAccess, adminsWithoutAccess }),
  ),
);

const adminsSharedWith = memoizeSelector((props: { candidateId: number }) =>
  createSelector(
    UserSelectors.entities(),
    selectors.byIdWithReferences(props.candidateId),
    (users: User[], candidate: FullCandidate) =>
      users.filter(user =>
        (candidate?.placements || []).some(placement =>
          user.accessiblePlacementIds?.includes(placement.id),
        ),
      ),
  ),
);

const FullCandidateSelectors = {
  ...selectors,
  byIdWithReferences: (id: number) =>
    createSelectorDeepEqual(selectors.byIdWithReferences(id), state => state),
  atcFeatureEnabled: createSelector(
    FeatureFlagSelectors.data,
    data => data[FeatureFlag.AccessToCandidate],
  ),
  commonAdminsGrouped,
  offerAdminsWithAccess,
  offerAdminsGrouped,
  adminsSharedWith,
};

export {
  FullCandidateActions,
  FullCandidateSelectors,
  reducer as FullCandidateReducer,
  namespace as FullCandidateNamespace,
};
